import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  CreditCard, Plus, Trash2, Edit, Eye, EyeOff, Save, X, 
  CheckCircle, AlertCircle, Loader2, Building2, Key
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

const StripeAccountsManager = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [showSecretKey, setShowSecretKey] = useState({});
  const [formData, setFormData] = useState({
    account_name: '',
    display_name: '',
    stripe_publishable_key: '',
    stripe_secret_key: '',
    description: ''
  });
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const token = localStorage.getItem('adminToken') || localStorage.getItem('livestreamToken');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/livestream/admin/stripe-accounts`, { headers });
      setAccounts(res.data.accounts || []);
    } catch (err) {
      console.error('Error fetching Stripe accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  const resetForm = () => {
    setFormData({
      account_name: '',
      display_name: '',
      stripe_publishable_key: '',
      stripe_secret_key: '',
      description: ''
    });
    setEditingAccount(null);
    setShowAddForm(false);
    setTestResult(null);
  };

  const handleEdit = (account) => {
    setEditingAccount(account);
    setFormData({
      account_name: account.account_name,
      display_name: account.display_name || '',
      stripe_publishable_key: account.stripe_publishable_key || '',
      stripe_secret_key: '', // Don't pre-fill secret key for security
      description: account.description || ''
    });
    setShowAddForm(true);
    setTestResult(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      // Generate account_name from display_name if not provided
      const accountName = formData.account_name || 
        formData.display_name.toLowerCase().replace(/[^a-z0-9]/g, '_');
      
      const payload = {
        ...formData,
        account_name: accountName
      };

      // Don't send empty secret key on edit (keep existing)
      if (editingAccount && !formData.stripe_secret_key) {
        delete payload.stripe_secret_key;
      }

      if (editingAccount) {
        await axios.put(
          `${API}/api/livestream/admin/stripe-accounts/${editingAccount.account_name}`,
          payload,
          { headers }
        );
      } else {
        await axios.post(`${API}/api/livestream/admin/stripe-accounts`, payload, { headers });
      }
      
      await fetchAccounts();
      resetForm();
    } catch (err) {
      console.error('Error saving Stripe account:', err);
      alert(err.response?.data?.detail || 'Failed to save Stripe account');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (accountName) => {
    if (!window.confirm(`Are you sure you want to delete the Stripe account "${accountName}"? This cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/api/livestream/admin/stripe-accounts/${accountName}`, { headers });
      await fetchAccounts();
    } catch (err) {
      console.error('Error deleting Stripe account:', err);
      alert(err.response?.data?.detail || 'Failed to delete Stripe account');
    }
  };

  const testConnection = async () => {
    if (!formData.stripe_secret_key && !editingAccount) {
      setTestResult({ success: false, message: 'Please enter a secret key to test' });
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const res = await axios.post(
        `${API}/api/livestream/admin/stripe-accounts/test`,
        { 
          stripe_secret_key: formData.stripe_secret_key || undefined,
          account_name: editingAccount?.account_name
        },
        { headers }
      );
      setTestResult({ success: true, message: res.data.message || 'Connection successful!' });
    } catch (err) {
      setTestResult({ 
        success: false, 
        message: err.response?.data?.detail || 'Connection failed. Please check your API keys.'
      });
    } finally {
      setTesting(false);
    }
  };

  const maskKey = (key) => {
    if (!key) return '••••••••';
    if (key.length <= 12) return '••••••••';
    return key.substring(0, 7) + '••••••••' + key.substring(key.length - 4);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <CreditCard className="h-6 w-6 text-purple-400" />
            Stripe Accounts
          </h2>
          <p className="text-gray-400 text-sm mt-1">
            Manage multiple Stripe accounts for different events and promoters
          </p>
        </div>
        {!showAddForm && (
          <Button 
            onClick={() => setShowAddForm(true)}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Stripe Account
          </Button>
        )}
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <Card className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 border-purple-500/30">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-white">
              <Key className="h-5 w-5 text-purple-400" />
              {editingAccount ? 'Edit Stripe Account' : 'Add New Stripe Account'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-200">Display Name *</Label>
                  <Input
                    value={formData.display_name}
                    onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                    className="bg-gray-800 border-gray-600 text-white mt-1 placeholder:text-gray-500"
                    placeholder="e.g., Main LTV Account, Promoter XYZ"
                    required
                  />
                  <p className="text-xs text-gray-400 mt-1">A friendly name to identify this account</p>
                </div>
                <div>
                  <Label className="text-gray-200">Account ID</Label>
                  <Input
                    value={formData.account_name}
                    onChange={(e) => setFormData({ ...formData, account_name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                    className="bg-gray-800 border-gray-600 text-white mt-1 placeholder:text-gray-500"
                    placeholder="Auto-generated from display name"
                    disabled={!!editingAccount}
                  />
                  <p className="text-xs text-gray-400 mt-1">Unique identifier (auto-generated if empty)</p>
                </div>
              </div>

              <div>
                <Label className="text-gray-200">Description</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="bg-gray-800 border-gray-600 text-white mt-1 placeholder:text-gray-500"
                  placeholder="e.g., For all hip-hop events, For promoter John's events"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-200">Publishable Key *</Label>
                  <Input
                    value={formData.stripe_publishable_key}
                    onChange={(e) => setFormData({ ...formData, stripe_publishable_key: e.target.value })}
                    className="bg-gray-800 border-gray-600 text-white mt-1 font-mono text-sm placeholder:text-gray-500"
                    placeholder="pk_live_..."
                    required={!editingAccount}
                  />
                  <p className="text-xs text-gray-400 mt-1">Starts with pk_live_ or pk_test_</p>
                </div>
                <div>
                  <Label className="text-gray-200">Secret Key {editingAccount ? '(leave empty to keep current)' : '*'}</Label>
                  <div className="relative">
                    <Input
                      type={showSecretKey.form ? 'text' : 'password'}
                      value={formData.stripe_secret_key}
                      onChange={(e) => setFormData({ ...formData, stripe_secret_key: e.target.value })}
                      className="bg-gray-800 border-gray-600 text-white mt-1 font-mono text-sm pr-10 placeholder:text-gray-500"
                      placeholder={editingAccount ? '••••••••••••' : 'sk_live_...'}
                      required={!editingAccount}
                    />
                    <button
                      type="button"
                      onClick={() => setShowSecretKey({ ...showSecretKey, form: !showSecretKey.form })}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-300 hover:text-white"
                    >
                      {showSecretKey.form ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Starts with sk_live_ or sk_test_</p>
                </div>
              </div>

              {/* Test Connection */}
              <div className="flex items-center gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={testConnection}
                  disabled={testing}
                  className="border-purple-500/50 text-white hover:bg-purple-500/20"
                >
                  {testing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                  Test Connection
                </Button>
                {testResult && (
                  <div className={`flex items-center gap-2 text-sm ${testResult.success ? 'text-green-400' : 'text-red-400'}`}>
                    {testResult.success ? <CheckCircle className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                    {testResult.message}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-gray-700">
                <Button
                  type="button"
                  variant="outline"
                  onClick={resetForm}
                  className="border-gray-600 text-white hover:bg-gray-700"
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-gradient-to-r from-purple-600 to-pink-600"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  {editingAccount ? 'Update Account' : 'Add Account'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Default Account Info */}
      <Card className="bg-blue-900/20 border-blue-500/30">
        <CardContent className="py-4">
          <div className="flex items-center gap-3">
            <Building2 className="h-5 w-5 text-blue-400" />
            <div>
              <p className="font-medium text-white">Default Account</p>
              <p className="text-sm text-gray-300">
                Events set to "default" will use the main Stripe account configured in your environment variables.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Accounts List */}
      <div className="grid gap-4">
        {accounts.length === 0 ? (
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="py-12 text-center">
              <CreditCard className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p className="text-gray-300">No additional Stripe accounts configured</p>
              <p className="text-sm text-gray-400 mt-1">Add accounts for different promoters or event types</p>
            </CardContent>
          </Card>
        ) : (
          accounts.map((account) => (
            <Card key={account.account_name} className="bg-gray-800/50 border-gray-700 hover:border-purple-500/50 transition-colors">
              <CardContent className="py-4">
                <div className="flex flex-col sm:flex-row justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <CreditCard className="h-5 w-5 text-purple-400" />
                      <h3 className="font-bold text-lg text-white">{account.display_name}</h3>
                      <Badge variant="outline" className="text-xs text-purple-300 border-purple-500/50">
                        {account.account_name}
                      </Badge>
                    </div>
                    {account.description && (
                      <p className="text-sm text-gray-300 mb-2">{account.description}</p>
                    )}
                    <div className="grid sm:grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400">Publishable:</span>
                        <code className="text-green-400 font-mono text-xs">
                          {maskKey(account.stripe_publishable_key)}
                        </code>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400">Secret:</span>
                        <code className="text-yellow-400 font-mono text-xs">
                          {showSecretKey[account.account_name] 
                            ? maskKey(account.stripe_secret_key)
                            : '••••••••••••••••'}
                        </code>
                        <button
                          onClick={() => setShowSecretKey({ 
                            ...showSecretKey, 
                            [account.account_name]: !showSecretKey[account.account_name] 
                          })}
                          className="text-gray-300 hover:text-white"
                        >
                          {showSecretKey[account.account_name] ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                        </button>
                      </div>
                    </div>
                    {account.events_count > 0 && (
                      <p className="text-xs text-gray-400 mt-2">
                        Used by {account.events_count} event{account.events_count !== 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                  <div className="flex sm:flex-col gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(account)}
                      className="border-gray-600 text-white hover:bg-gray-700"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(account.account_name)}
                      className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default StripeAccountsManager;
